import type { Department, Device } from '@budovan/domain/proposed';

/* Typed demo data for screens whose module has no API yet (devices, departments). The types are the
 * proposed domain model (@budovan/domain/proposed), and demo.test.ts checks every row against its
 * schema, so the screens are built against the shapes the API will return. Rows are derived from real
 * buildings and spaces, so codes and ids line up with the rest of the console. */

/** A stable UUID-shaped id from a seed, so demo rows keep their identity across reloads. */
export function demoId(seed: string): string {
  let a = 0x811c9dc5;
  let b = 0x01000193;
  const hex: string[] = [];
  for (let round = 0; round < 4; round += 1) {
    for (const char of `${seed}:${round}`) {
      a = Math.imul(a ^ char.charCodeAt(0), 0x01000193) >>> 0;
      b = Math.imul(b + char.charCodeAt(0), 0x5bd1e995) >>> 0;
    }
    hex.push(((a ^ b) >>> 0).toString(16).padStart(8, '0'));
  }
  const raw = hex.join('');
  return `${raw.slice(0, 8)}-${raw.slice(8, 12)}-4${raw.slice(13, 16)}-a${raw.slice(17, 20)}-${raw.slice(20, 32)}`;
}

interface PlaceLike {
  code: string;
  kind: string;
  name: string;
}

const ROOM_EQUIPMENT: { kind: Device['kind']; name: string }[] = [
  { kind: 'light', name: 'Ceiling lights' },
  { kind: 'shade', name: 'Window shades' },
  { kind: 'fin', name: 'Fins' },
  { kind: 'thermostat', name: 'Thermostat' },
];

/** Room-control equipment in each meeting room, as the Room control screens show it. */
export function demoDevices(building: { id: string }, spaces: PlaceLike[]): Device[] {
  return spaces
    .filter((space) => space.kind === 'meeting_room' || space.kind === 'workspace_area')
    .flatMap((space) =>
      (space.kind === 'meeting_room' ? ROOM_EQUIPMENT : ROOM_EQUIPMENT.slice(0, 1)).map((item, index) => ({
        id: demoId(`${space.code}-${item.kind}`),
        code: `${space.code}-${item.kind.toUpperCase().slice(0, 3)}${index + 1}`,
        buildingId: building.id,
        placeCode: space.code,
        kind: item.kind,
        name: `${item.name}, ${space.name}`,
      })),
    );
}

const DEPARTMENTS: [string, string][] = [
  ['Engineering', '#2E90FA'],
  ['Sales', '#F79009'],
  ['Marketing', '#EE46BC'],
  ['Finance', '#17B069'],
  ['Operations', '#7A5AF8'],
  ['People', '#F04438'],
];

/** The departments of the Figma Departments screen, for one organization. */
export function demoDepartments(organizationId: string): Department[] {
  return DEPARTMENTS.map(([name, color]) => ({
    id: demoId(`${organizationId}-${name}`),
    organizationId,
    name,
    color,
    createdAt: '2026-10-01T08:00:00.000Z',
  }));
}
